import { useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import Toast from '@/utils/toastManager';
import './UserAnalyticsModal.css';

const fmt = ts =>
	ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

const formatLabel = value =>
	value
		? String(value)
				.replace(/([A-Z])/g, ' $1')
				.replace(/_/g, ' ')
				.replace(/\s+/g, ' ')
				.trim()
				.replace(/^./, s => s.toUpperCase())
		: 'N/A';

const buildProfile = rawProfile => {
	if (!rawProfile) return rawProfile;

	const subscription = rawProfile.subscription && typeof rawProfile.subscription === 'object' ? rawProfile.subscription : {};

	return {
		...rawProfile,
		...subscription,
		nextBillingAt: rawProfile.nextBillingAt || rawProfile.next_billing_at || subscription.nextBillingAt || subscription.next_billing_at || null,
		trialEnd: rawProfile.trialEnd || rawProfile.trial_end || subscription.trialEnd || subscription.trial_end || null,
		subscription
	};
};

const getPlanDisplayName = profile => {
	if (!profile) return 'No active plan';

	const raw = [
		profile.subscription_name,
		profile.subscription?.subscription_name,
		profile.planName,
		profile.plan
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();

	if (profile.userType === 'vip' || raw.includes('vip') || raw.includes('plus')) return 'Plus';
	if (profile.userType === 'studio-owner' || raw.includes('studio')) return 'Studio';
	if (raw.includes('standard') || raw.includes('individual')) return 'Standard';

	const name = profile.subscription_name || profile.subscription?.subscription_name || profile.planName || profile.plan;
	return name ? formatLabel(name) : 'No active plan';
};

const Stat = ({ label, value }) => (
	<div className="ua-stat">
		<div className="ua-stat-label">{label}</div>
		<div className="ua-stat-value">{value ?? 'N/A'}</div>
	</div>
);

const SectionTitle = ({ eyebrow, title, description }) => (
	<div className="ua-section-heading">
		{eyebrow && <div className="ua-section-eyebrow">{eyebrow}</div>}
		<div className="ua-section-title-row">
			<h2>{title}</h2>
			{description && <p>{description}</p>}
		</div>
	</div>
);

const StatusChip = ({ status }) => {
	const cls = ['cancelled', 'active', 'scheduled'].includes(status) ? status : 'default';
	return <span className={`ua-chip ${cls}`}>{formatLabel(status)}</span>;
};

const BadgeSection = ({ title, badges }) => {
	if (!badges || !Object.keys(badges).length) return null;

	return (
		<div className="ua-badge-section">
			<div className="ua-badge-section-title">{title}</div>
			<div className="ua-badges-grid">
				{Object.entries(badges).map(([key, data]) => (
					<div key={key} className={`ua-badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
						{data?.achieved ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
						<span>{formatLabel(key)}</span>
						{data?.achieved && data?.date && <span className="ua-badge-date">{fmt(data.date)}</span>}
					</div>
				))}
			</div>
		</div>
	);
};

export default function UserAnalyticsModal({ user, open, onClose }) {
	const [analyticsData, setAnalyticsData] = useState(null);
	const [analyticsLoading, setAnalyticsLoading] = useState(false);
	const [analyticsError, setAnalyticsError] = useState('');

	useEffect(() => {
		if (!open || !user?.email) return undefined;

		let active = true;

		const loadAnalytics = async () => {
			setAnalyticsData(null);
			setAnalyticsError('');
			setAnalyticsLoading(true);

			try {
				const profileRes = await axiosConfig.get(`/admin/user/${encodeURIComponent(user.email)}`);
				const profile = buildProfile(profileRes.data?.data || profileRes.data);

				const userId = profile?.userId || user.userId || user._id;
				const email = profile?.email || user.email;

				const [activityRes, engagementRes] = await Promise.allSettled([
					userId ? axiosConfig.get(`/user-analytics/${userId}`) : Promise.resolve(null),
					axiosConfig.get(`/admin/engagement/user/${encodeURIComponent(email)}`)
				]);

				if (!active) return;

				const activityJson = activityRes.status === 'fulfilled' ? activityRes.value?.data : null;
				const engagementJson = engagementRes.status === 'fulfilled' ? engagementRes.value?.data : null;

				setAnalyticsData({
					profile: profile || user,
					activity: activityJson?.data || activityJson,
					engagement: engagementJson?.success ? engagementJson.data : engagementJson?.data || null
				});
			} catch (err) {
				if (!active) return;
				const msg = err?.response?.data?.error || err?.response?.data?.message || err.message;
				setAnalyticsError(msg || 'Failed to load analytics');
				Toast.error(msg || 'Failed to load analytics');
			} finally {
				if (active) {
					setAnalyticsLoading(false);
				}
			}
		};

		loadAnalytics();

		return () => {
			active = false;
		};
	}, [open, user]);

	const { profile, activity, engagement } = analyticsData || {};
	const modalUser = profile || user;
	const headerName = `${modalUser?.firstname || ''} ${modalUser?.lastname || ''}`.trim() || 'User overview';

	const topSummaryStats = useMemo(
		() =>
			profile
				? [
						{ label: 'Subscription', value: getPlanDisplayName(profile) },
						{ label: 'Status', value: <StatusChip status={profile.subscriptionStatus} /> },
						{ label: 'Member Since', value: fmt(profile.createdAt || user?.createdAt) },
						{ label: 'Current Level', value: profile.onboardingPreferences?.currentLevel || 'N/A' }
					]
				: [],
		[profile, user]
	);

	if (!open || !user) return null;

	return (
		<div className="ua-modal-overlay" onClick={onClose}>
			<div className="ua-modal" onClick={e => e.stopPropagation()}>
				<div className="ua-modal-header">
					<div className="ua-modal-hero">
						<div className="ua-modal-eyebrow">User Analytics</div>
						<div className="ua-modal-title-row">
							<div>
								<div className="ua-modal-title">{headerName}</div>
								<div className="ua-modal-subtitle">{modalUser?.email || user.email}</div>
							</div>
							{profile?.subscriptionStatus && <StatusChip status={profile.subscriptionStatus} />}
						</div>
						{profile && (
							<div className="ua-summary-grid">
								{topSummaryStats.map(item => (
									<div key={item.label} className="ua-summary-card">
										<span>{item.label}</span>
										<strong>{item.value}</strong>
									</div>
								))}
							</div>
						)}
					</div>
					<button className="ua-panel-close" onClick={onClose}>
						✕
					</button>
				</div>

				<div className="ua-modal-body">
					{analyticsLoading && <div className="ua-loading">Loading analytics...</div>}
					{analyticsError && <div className="ua-error">{analyticsError}</div>}

					{analyticsData && (
						<div className="ua-sections">
							<div className="ua-card">
								<SectionTitle
									eyebrow="Profile"
									title="Account details"
									description="Core identity and onboarding data for this member."
								/>
								<div className="ua-grid">
									<Stat label="Name" value={`${profile.firstname || ''} ${profile.lastname || ''}`.trim()} />
									<Stat label="Email" value={profile.email} />
									<Stat label="User ID" value={profile.userId} />
									<Stat label="User Type" value={profile.userType} />
									<Stat label="Role" value={profile.onboardingRole} />
									<Stat label="Member Since" value={fmt(profile.createdAt)} />
									<Stat label="Onboarding Completed" value={profile.onboardingCompleted ? 'Yes' : 'No'} />
									<Stat label="Current Level" value={profile.onboardingPreferences?.currentLevel} />
								</div>
							</div>

							<div className="ua-card">
								<SectionTitle
									eyebrow="Billing"
									title="Subscription and plan"
									description="Current plan metadata, billing cadence, and Chargebee identifiers."
								/>
								<div className="ua-grid">
									<Stat label="Plan Name" value={getPlanDisplayName(profile)} />
									<Stat label="Plan ID" value={profile.SubscribtionId || profile.subscription?.SubscribtionId} />
									<Stat label="Subscription Status" value={profile.subscriptionStatus || profile.subscription?.subscriptionStatus} />
									<Stat label="Subscription ID" value={profile.SubscribtionId || profile.subscriptionId || profile.subscription?.SubscribtionId} />
									<Stat label="Chargebee Customer ID" value={profile.CustomerID || profile.chargebeeCustomerId || profile.subscription?.CustomerID} />
									<Stat label="Billing Cycle" value={profile.billingCycle || profile.billing_period_unit || profile.subscription?.billing_period_unit} />
									<Stat label="Next Billing" value={profile.nextBillingAt ? fmt(Number(profile.nextBillingAt) * 1000) : null} />
									<Stat label="Trial End" value={
										profile.subscriptionStatus === 'in_trial' && profile.nextBillingAt
											? fmt(Number(profile.nextBillingAt) * 1000)
											: profile.isTrialAvailed ? 'Trial used' : null
									} />
								</div>
							</div>

							{activity && (
								<div className="ua-card">
									<SectionTitle
										eyebrow="Usage"
										title="Activity summary"
										description="High-level product activity based on login, watch, and completion events."
									/>
									<div className="ua-grid">
										<Stat label="Total Logins" value={activity.totalLogins} />
										<Stat label="Login Streak" value={activity.loginStreak != null ? `${activity.loginStreak} days` : null} />
										<Stat label="Videos Watched" value={activity.videosWatched} />
										<Stat
											label="Total Watch Time"
											value={activity.totalWatchTimeSeconds != null ? `${Math.round(activity.totalWatchTimeSeconds / 60)} min` : null}
										/>
										<Stat label="Playlists Created" value={activity.playlistsCreated} />
										<Stat label="Videos Saved" value={activity.videosSaved} />
										<Stat label="Classes Completed" value={activity.classesCompleted} />
										<Stat label="Exercises Completed" value={activity.exercisesCompleted} />
										<Stat label="Lessons Completed" value={activity.lessonsCompleted} />
										<Stat label="Community Comments" value={activity.communityComments} />
										<Stat label="Live Classes Attended" value={activity.liveClassesAttended} />
									</div>
								</div>
							)}

							{engagement && !engagement.error && (
								<div className="ua-card">
									<SectionTitle
										eyebrow="Engagement"
										title="Milestones and retention signals"
										description="Watch-hour progress, streaks, membership longevity, and earned badges."
									/>
									<div className="ua-grid ua-grid--spaced">
										<Stat label="Total Watch Hours" value={engagement.summary?.totalWatchHours} />
										<Stat label="Videos Watched" value={engagement.summary?.videosWatched} />
										<Stat
											label="Current Login Streak"
											value={engagement.summary?.currentLoginStreak != null ? `${engagement.summary.currentLoginStreak} days` : null}
										/>
										<Stat
											label="Longest Login Streak"
											value={engagement.summary?.longestLoginStreak != null ? `${engagement.summary.longestLoginStreak} days` : null}
										/>
										<Stat
											label="Membership Days"
											value={engagement.summary?.membershipDays != null ? `${engagement.summary.membershipDays} days` : null}
										/>
									</div>
									{engagement.badges && (
										<>
											<BadgeSection title="Kickoff Badges" badges={engagement.badges.kickoffBadges} />
											<BadgeSection title="Watch Hour Milestones" badges={engagement.badges.watchHourMilestones} />
											<BadgeSection title="Login Milestones" badges={engagement.badges.loginMilestones} />
											<BadgeSection title="General Milestones" badges={engagement.badges.generalMilestones} />
											<BadgeSection title="Anniversary Badges" badges={engagement.badges.anniversaries} />
										</>
									)}
								</div>
							)}

							{profile.cancellationHistory?.length > 0 && (
								<div className="ua-card">
									<SectionTitle
										eyebrow="Retention"
										title={`Cancellation history (${profile.cancellationHistory.length})`}
										description="Previous cancellation attempts, outcomes, and likelihood-to-return signals."
									/>
									<div className="ua-cancellations-list">
										{profile.cancellationHistory.map((item, idx) => (
											<div key={idx} className="ua-cancellation-item">
												<div>
													<div className="date">{fmt(item.createdAt)}</div>
													<div className="reason">{item.reason || 'No reason provided'}</div>
													{item.otherText && <div className="ua-cancellation-note">{item.otherText}</div>}
												</div>
												<div className="ua-cancellation-meta">
													<StatusChip status={item.statusAfter} />
													{item.cancelOption && <span>{formatLabel(item.cancelOption)}</span>}
													{item.likelihood != null && <span>Return likelihood: {item.likelihood}/10</span>}
												</div>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
